// ============================================
// Top Navigation Bar
// Uses global layout classes from _layout.scss
// ============================================

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Popover from '@components/ui/Popover/Popover';
import Avatar from '@components/ui/Avatar/Avatar';
import ProfileImageEditor from '@components/shared/ProfileImageEditor';
import Select from '@workpay/ds-shared/components/ui/Select';
import NotificationBell from '@components/shared/NotificationBell/NotificationBell';
import { useAuthStore } from '@/stores/authStore';
import { useLocalizationStore } from '@/stores/localizationStore';
import { logout } from '@/services/authService';
import { uploadFile } from '@/services/fileService';
import { getCurrentUser, setUserProfileImage, clearUserProfileImage } from '@/services/userService';
import { broadcastLogout } from '@/hooks/useAuthBroadcast';
import { getAvailableLanguages } from '@/services/localizationService';
import { useTranslation } from '@/hooks/useTranslation';

import type { SelectOption } from '@workpay/ds-shared/components/ui/Select';

// Caret down icon
function CaretDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 6L8 10L12 6"
        stroke="#536171"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Logout icon (arrow exiting box)
function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5M13.3333 14.1667L17.5 10M17.5 10L13.3333 5.83333M17.5 10H7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Globe icon for language selector
function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2 12H22M12 2C14.5 4.5 15.5 8 15.5 12C15.5 16 14.5 19.5 12 22M12 2C9.5 4.5 8.5 8 8.5 12C8.5 16 9.5 19.5 12 22"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// session.identity.roles is gone — derive the badge label from user.type.
// Only ADMIN vs default — matches prior behaviour (SUPER_ADMIN was a Keycloak
// role name, not a user.type, and is no longer exposed).
function getRoleLabel(
  userType: import('@/types/session').UserType | null | undefined,
  t: (key: string, fallback?: string) => string,
): string {
  if (userType === 'ADMIN') return t('K_ROLE_ADMIN', 'Admin');
  return t('K_ROLE_USER', 'User');
}

/** Map locale codes to display names using browser-native Intl API */
function getLanguageDisplayName(locale: string): string {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    const name = displayNames.of(locale);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : locale;
  } catch {
    return locale;
  }
}

export interface TopNavProps {
  collapsed?: boolean;
}

export function TopNav({ collapsed }: TopNavProps) {
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session);
  const clearSession = useAuthStore((s) => s.clearSession);
  const language = useLocalizationStore((s) => s.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // /users/me carries profileImagePath/profileImageUrl. Session payload doesn't,
  // so a separate query owns it and is the source of truth for "current avatar URL".
  const { data: me } = useQuery({
    queryKey: ['userMe'],
    queryFn: getCurrentUser,
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
  });

  const platformUserId = me?.platformUserId ?? null;
  const avatarUrl = me?.profileImageUrl ?? null;

  // Self-edit save: upload bytes → backend returns `bucketPath` → link as `path`
  // on users.profile_image_path. file-management-service has no callback into
  // platform-service; the FE owns both legs. Invalidate ['userMe'] so the navbar
  // avatar re-renders with the new profileImageUrl.
  const handleProfileImageSave = useCallback(
    async (blob: Blob) => {
      if (!platformUserId) {
        throw new Error('User identity not loaded yet — try again in a moment.');
      }
      const file = new File([blob], `profile-${platformUserId}-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
      const uploadStarted = performance.now();
      const uploaded = await uploadFile(file, {
        workflow: 'PROFILE_IMAGE',
        objectType: 'USER',
        objectId: platformUserId,
      });
      if (!uploaded.bucketPath) {
        throw new Error(
          'Upload response missing bucketPath — file-management-service may not be running ' +
            'a recent enough build, or the PROFILE_IMAGE workflow is not configured to use the public bucket.',
        );
      }
      const linkStarted = performance.now();
      const updated = await setUserProfileImage(platformUserId, uploaded.bucketPath);
      console.log('[ProfileImage upload] self', {
        platformUserId,
        uploadedFileId: uploaded.id,
        fileName: uploaded.fileName,
        contentType: uploaded.contentType,
        sizeBytes: uploaded.size,
        sizeKb: (uploaded.size / 1024).toFixed(1),
        bucketPath: uploaded.bucketPath,
        uploadMs: Math.round(linkStarted - uploadStarted),
        linkMs: Math.round(performance.now() - linkStarted),
        finalProfileImagePath: updated.profileImagePath,
        finalProfileImageUrl: updated.profileImageUrl,
      });
      void queryClient.invalidateQueries({ queryKey: ['userMe'] });
    },
    [platformUserId, queryClient, t],
  );

  const handleProfileImageRemove = useCallback(async () => {
    if (!platformUserId) return;
    await clearUserProfileImage(platformUserId);
    void queryClient.invalidateQueries({ queryKey: ['userMe'] });
  }, [platformUserId, queryClient]);

  const canEditOwnAvatar = hasPermission('USERS_UPDATE') && !!platformUserId;

  const { data: languages } = useQuery({
    queryKey: ['localization', 'languages'],
    queryFn: getAvailableLanguages,
    staleTime: 10 * 60 * 1000,
  });

  const languageOptions: SelectOption[] = (languages ?? ['en-US']).map((lang) => ({
    label: getLanguageDisplayName(lang),
    value: lang,
  }));

  const firstName = session?.user.firstName ?? '';
  const lastName = session?.user.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const email = session?.user.email ?? '';
  const roleLabel = session ? getRoleLabel(session.user.type, t) : '';

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setDropdownOpen(false);
    try {
      await logout();
    } finally {
      // Order matters. clearSession() flips authStore.session to null but does
      // not touch the TanStack Query cache, so __root.tsx's effect re-hydrates
      // from cached data on the very next render (`if (data && !session)` is
      // true again) — racing window.location.href. Removing the cached session
      // before clearing the store closes the race.
      queryClient.removeQueries({ queryKey: ['session'] });
      broadcastLogout();
      clearSession();
      const redirectPath = window.location.pathname + window.location.search;
      const loginUrl =
        redirectPath && redirectPath !== '/' && redirectPath !== '/login'
          ? `/login?redirect_to=${encodeURIComponent(redirectPath)}`
          : '/login';
      window.location.href = loginUrl;
    }
  }, [clearSession, queryClient, isLoggingOut]);

  const dropdownContent = (
    <div className="profile-dropdown">
      <div className="profile-dropdown__header">
        <ProfileImageEditor
          displayName={fullName || t('K_FALLBACK_USER', 'User')}
          size="md"
          currentImageUrl={avatarUrl}
          canEdit={canEditOwnAvatar}
          onSave={handleProfileImageSave}
          onRemove={handleProfileImageRemove}
          captureMode="user"
        />
        <div className="profile-dropdown__user-info">
          <span className="profile-dropdown__name">{fullName || t('K_FALLBACK_USER', 'User')}</span>
          <span className="profile-dropdown__role">
            {email}
            {roleLabel && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: '#EBF5E6',
                  color: '#62A446',
                  marginLeft: '4px',
                }}
              >
                {roleLabel}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="profile-dropdown__items">
        <button
          type="button"
          className="profile-dropdown__item"
          onClick={() => void handleLogout()}
          disabled={isLoggingOut}
          aria-busy={isLoggingOut}
        >
          <LogoutIcon />
          {isLoggingOut ? t('K_LOGGING_OUT', 'Logging out...') : t('K_LOGOUT', 'Logout')}
        </button>
      </div>
    </div>
  );

  return (
    <header className="topnav" style={{ left: collapsed ? '72px' : '216px' }}>
      <div className="topnav__left" />

      <div className="topnav__right">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
          <GlobeIcon />
          <Select
            options={languageOptions}
            value={language}
            onChange={setLanguage}
            size="sm"
          />
        </div>
        <NotificationBell />
        <Popover
          content={dropdownContent}
          placement="bottom-end"
          trigger="click"
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
          className="profile-dropdown-popover"
        >
          <div className="topnav__profile">
            <Avatar name={fullName} size="sm" {...(avatarUrl ? { src: avatarUrl } : {})} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span
                style={{
                  fontFamily: "'Moderat', sans-serif",
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#253545',
                  maxWidth: '160px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName || t('K_FALLBACK_USER', 'User')}
              </span>
              <span
                style={{
                  fontFamily: "'Moderat', sans-serif",
                  fontSize: '14px',
                  color: '#62A446',
                }}
              >
                {roleLabel || t('K_ROLE_USER', 'User')}
              </span>
            </div>
            <CaretDownIcon />
          </div>
        </Popover>
      </div>
    </header>
  );
}
